import type { ComboOption } from '@/components/ui/combobox'

// Ícones curados para categorias/contas — lista de SELEÇÃO (não texto livre).
// `value` é o emoji gravado no banco; precisa ser ÚNICO na lista (senão o
// Combobox não sabe marcar o item certo como selecionado). `keywords` amplia a
// busca sem duplicar o valor (ex.: 💵 aparece buscando "salário" OU "cédulas").
interface IconDef {
  emoji: string
  name: string
  keywords?: string
}
interface IconGroup {
  header: string
  icons: IconDef[]
}

const GROUPS: IconGroup[] = [
  {
    header: 'Financeiro',
    icons: [
      { emoji: '🏦', name: 'Banco' },
      { emoji: '💳', name: 'Cartão' },
      { emoji: '💰', name: 'Dinheiro', keywords: 'cofre poupança' },
      { emoji: '💵', name: 'Cédulas', keywords: 'salário renda' },
      { emoji: '🪙', name: 'Moedas' },
      { emoji: '📈', name: 'Investimentos', keywords: 'ganhos' },
      { emoji: '📉', name: 'Perdas', keywords: 'prejuízo' },
      { emoji: '💹', name: 'Câmbio', keywords: 'cotação moeda' },
      { emoji: '🧾', name: 'Recibo', keywords: 'fatura nota' },
      { emoji: '🏧', name: 'Caixa eletrônico', keywords: 'saque' },
      { emoji: '🐷', name: 'Poupança' },
      { emoji: '👛', name: 'Carteira' },
      { emoji: '🤝', name: 'Empréstimo', keywords: 'financiamento acordo' },
      { emoji: '💸', name: 'Taxas', keywords: 'juros encargos' },
    ],
  },
  {
    header: 'Casa',
    icons: [
      { emoji: '🏠', name: 'Casa', keywords: 'moradia aluguel' },
      { emoji: '🏢', name: 'Prédio', keywords: 'condomínio' },
      { emoji: '🔑', name: 'Chaves', keywords: 'aluguel' },
      { emoji: '🛠️', name: 'Manutenção', keywords: 'reparo' },
      { emoji: '💡', name: 'Energia', keywords: 'luz eletricidade' },
      { emoji: '🔥', name: 'Gás' },
      { emoji: '💧', name: 'Água' },
      { emoji: '🌐', name: 'Internet', keywords: 'wifi' },
      { emoji: '📞', name: 'Telefone' },
    ],
  },
  {
    header: 'Transporte',
    icons: [
      { emoji: '🚗', name: 'Carro', keywords: 'veículo' },
      { emoji: '🚕', name: 'Táxi', keywords: 'uber corrida' },
      { emoji: '🚌', name: 'Transporte público', keywords: 'ônibus' },
      { emoji: '🚲', name: 'Bicicleta' },
      { emoji: '⛽', name: 'Combustível', keywords: 'gasolina' },
      { emoji: '🅿️', name: 'Estacionamento' },
      { emoji: '✈️', name: 'Viagem aérea', keywords: 'voo' },
      { emoji: '🧳', name: 'Viagem', keywords: 'mala' },
    ],
  },
  {
    header: 'Alimentação',
    icons: [
      { emoji: '🍔', name: 'Alimentação', keywords: 'comida' },
      { emoji: '🍽️', name: 'Restaurante' },
      { emoji: '☕', name: 'Café' },
      { emoji: '🛒', name: 'Mercado', keywords: 'supermercado' },
      { emoji: '📦', name: 'Entrega', keywords: 'delivery' },
      { emoji: '🍸', name: 'Bar', keywords: 'bebidas' },
    ],
  },
  {
    header: 'Família',
    icons: [
      { emoji: '👨‍👩‍👧‍👦', name: 'Família' },
      { emoji: '👶', name: 'Filhos', keywords: 'bebê' },
      { emoji: '🎓', name: 'Educação', keywords: 'escola faculdade' },
      { emoji: '🧸', name: 'Brinquedos' },
      { emoji: '🐾', name: 'Pets', keywords: 'animais' },
    ],
  },
  {
    header: 'Saúde e bem-estar',
    icons: [
      { emoji: '⚕️', name: 'Saúde', keywords: 'médico' },
      { emoji: '🏋️', name: 'Academia', keywords: 'fitness' },
      { emoji: '💅', name: 'Beleza' },
      { emoji: '📖', name: 'Estudos', keywords: 'livros' },
    ],
  },
  {
    header: 'Lazer',
    icons: [
      { emoji: '🎉', name: 'Festa', keywords: 'lazer' },
      { emoji: '🎮', name: 'Jogos' },
      { emoji: '🎬', name: 'Cinema' },
      { emoji: '📺', name: 'Streaming' },
      { emoji: '🎟️', name: 'Eventos', keywords: 'ingressos' },
      { emoji: '🎰', name: 'Apostas' },
    ],
  },
  {
    header: 'Compras',
    icons: [
      { emoji: '🛍️', name: 'Compras' },
      { emoji: '👗', name: 'Roupas' },
      { emoji: '💻', name: 'Eletrônicos' },
      { emoji: '🪑', name: 'Móveis' },
      { emoji: '💝', name: 'Presentes' },
    ],
  },
  {
    header: 'Trabalho e outros',
    icons: [
      { emoji: '💼', name: 'Trabalho' },
      { emoji: '🎁', name: 'Bônus' },
      { emoji: '🧑‍💼', name: 'Serviços profissionais' },
      { emoji: '⚖️', name: 'Jurídico', keywords: 'impostos' },
      { emoji: '✂️', name: 'Serviços' },
      { emoji: '📋', name: 'Documentos' },
    ],
  },
]

// Achata em ComboOption[] com cabeçalhos de grupo. `noneLabel` é o texto da
// opção "sem ícone"/"padrão" — quem chama decide o texto (contexto muda entre
// categoria e conta).
export function buildIconOptions(noneLabel: string): ComboOption[] {
  const out: ComboOption[] = [{ value: '', label: noneLabel }]
  for (const g of GROUPS) {
    out.push({ value: `__h_${g.header}`, label: g.header, header: true })
    for (const icon of g.icons) {
      out.push({ value: icon.emoji, label: `${icon.emoji}  ${icon.name}`, keywords: icon.keywords })
    }
  }
  return out
}
